import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Modal,
  Radio,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SignableAction } from '../types';
import { MOCK_BEHAVIOR } from './mockBehavior';
import { keyProtectionLabel, providerLabel, purposeLabel } from './mockCerts';
import {
  SimulationError,
  type CertProvider,
  type SessionResult,
  type SimulatedCert,
} from './types';

/** Synthetic option on the credential picker that routes into the card flow. */
const CAC_OPTION_ID = '__cac__';

type StepKey =
  | 'choose'
  | 'insert'
  | 'pick-cert'
  | 'enter-pin'
  | 'win-security'
  | 'win-sign';

type StepDef = { key: StepKey; label: string };

const CHOOSE_STEP: StepDef = { key: 'choose', label: 'Choose credential' };

// The original three CAC pages, now preceded by the credential picker.
const CAC_STEPS: StepDef[] = [
  CHOOSE_STEP,
  { key: 'insert', label: 'Insert CAC' },
  { key: 'pick-cert', label: 'Select certificate' },
  { key: 'enter-pin', label: 'Enter PIN' },
];

// Windows-store signing diverges: no card, no card PIN — OS key access instead.
const WINDOWS_STEPS: StepDef[] = [
  CHOOSE_STEP,
  { key: 'win-security', label: 'Windows Security' },
  { key: 'win-sign', label: 'Sign' },
];

const DialogContainer = styled(Box, {
  name: 'cac-sim-dialog',
  slot: 'Root',
})(({ theme }) => {
  const tokens = theme.vars ?? theme;
  return {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '720px',
    maxWidth: '92vw',
    backgroundColor: tokens.palette.background.paper,
    color: tokens.palette.text.primary,
    border: `2px solid ${tokens.palette.divider}`,
    boxShadow: theme.shadows[24],
  };
});

type Props = {
  open: boolean;
  action: SignableAction;
  /** On-card CAC certificates (used inside the card sub-flow). */
  certs: SimulatedCert[];
  /** Certificates available in the Windows certificate store. */
  windowsCerts: SimulatedCert[];
  onConfirm: (result: SessionResult) => void;
  onReject: (err: SimulationError) => void;
};

/** Shared row UI for the radio-style selectable cert lists. */
function SelectableRow({
  selected,
  allowed,
  onSelect,
  children,
}: {
  selected: boolean;
  allowed: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <Box
      onClick={() => allowed && onSelect()}
      sx={(theme) => {
        const tokens = theme.vars ?? theme;
        return {
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: 1,
          border: `1px solid ${
            selected ? tokens.palette.primary.main : tokens.palette.divider
          }`,
          backgroundColor: selected
            ? tokens.palette.action.selected
            : 'transparent',
          color: allowed
            ? tokens.palette.text.primary
            : tokens.palette.text.disabled,
          cursor: allowed ? 'pointer' : 'not-allowed',
          transition: 'background-color 120ms ease, border-color 120ms ease',
          '&:hover': allowed
            ? { backgroundColor: tokens.palette.action.hover }
            : undefined,
        };
      }}
    >
      <Radio checked={selected} disabled={!allowed} size="small" tabIndex={-1} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

export default function CacSimulationDialog({
  open,
  action,
  certs,
  windowsCerts,
  onConfirm,
  onReject,
}: Props) {
  const [activeStep, setActiveStep] = useState(0);
  // Front-page credential choice (a Windows cert id, or the CAC option).
  const [selectedCredId, setSelectedCredId] = useState<string>('');
  // CAC sub-flow: the on-card certificate.
  const [selectedCertId, setSelectedCertId] = useState<string>('');
  const [pin, setPin] = useState('');
  const [pinAttempts, setPinAttempts] = useState(0);
  const [pinError, setPinError] = useState<string | null>(null);
  const [insertError, setInsertError] = useState<string | null>(null);
  const [readingCard, setReadingCard] = useState(false);
  // Windows sub-flow: strong-key password.
  const [keyPassword, setKeyPassword] = useState('');
  const [keyAttempts, setKeyAttempts] = useState(0);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setSelectedCredId('');
      setSelectedCertId('');
      setPin('');
      setPinAttempts(0);
      setPinError(null);
      setInsertError(null);
      setReadingCard(false);
      setKeyPassword('');
      setKeyAttempts(0);
      setKeyError(null);
      setSubmitting(false);
    }
  }, [open]);

  const provider: CertProvider | null =
    selectedCredId === ''
      ? null
      : selectedCredId === CAC_OPTION_ID
        ? 'cac'
        : 'windows';

  const selectedWindowsCert = useMemo(
    () => windowsCerts.find((c) => c.id === selectedCredId) ?? null,
    [windowsCerts, selectedCredId],
  );

  const steps = provider === 'windows' ? WINDOWS_STEPS : CAC_STEPS;
  const currentStep = steps[activeStep]?.key ?? 'choose';

  // Front page: a credential is pickable only if valid for this action.
  const canChooseNext =
    provider === 'cac' ||
    (provider === 'windows' &&
      selectedWindowsCert !== null &&
      selectedWindowsCert.validForActions.includes(action));

  // CAC sub-flow: the chosen on-card certificate.
  const selectedCert = useMemo(
    () => certs.find((c) => c.id === selectedCertId) ?? null,
    [certs, selectedCertId],
  );
  const canPickCert =
    selectedCert !== null && selectedCert.validForActions.includes(action);

  const handleCancel = () => {
    if (submitting) return;
    onReject(new SimulationError('USER_CANCELLED'));
  };

  const handleChooseNext = () => {
    if (!canChooseNext) return;
    if (provider === 'cac') {
      // Entering the card flow — surface a missing-card error up front.
      setInsertError(
        MOCK_BEHAVIOR.noCard()
          ? 'No CAC detected. Insert a card and try again.'
          : null,
      );
    }
    setActiveStep(1);
  };

  const handleInsertNext = () => {
    if (MOCK_BEHAVIOR.noCard()) {
      setInsertError('No CAC detected. Insert a card and try again.');
      return;
    }
    setReadingCard(true);
    setInsertError(null);
    window.setTimeout(() => {
      setReadingCard(false);
      setActiveStep(2);
    }, 350);
  };

  const handleCertNext = () => {
    if (!canPickCert) return;
    setActiveStep(3);
  };

  const handlePinSubmit = () => {
    if (pin.length < 4) {
      setPinError('Enter your PIN to continue.');
      return;
    }
    const rejecting = MOCK_BEHAVIOR.pinRejects();
    const wrong = rejecting !== undefined && pin === rejecting;
    if (wrong) {
      const nextAttempts = pinAttempts + 1;
      setPinAttempts(nextAttempts);
      const remaining = MOCK_BEHAVIOR.maxPinAttempts - nextAttempts;
      if (remaining <= 0) {
        onReject(new SimulationError('PIN_LOCKED'));
        return;
      }
      setPinError(`PIN incorrect — ${remaining} attempts remaining.`);
      setPin('');
      return;
    }
    setSubmitting(true);
    setPinError(null);
    window.setTimeout(() => {
      if (!selectedCert) {
        setSubmitting(false);
        onReject(new SimulationError('NO_CARD'));
        return;
      }
      onConfirm({ cert: selectedCert, pin });
    }, MOCK_BEHAVIOR.signLatencyMs);
  };

  // Windows: grant the app access to the private key (consent / password / Hello).
  const handleWindowsAllow = () => {
    const cert = selectedWindowsCert;
    if (!cert) return;
    if (cert.keyProtection === 'password') {
      if (keyPassword.length < 1) {
        setKeyError('Enter the private key password.');
        return;
      }
      const reject = MOCK_BEHAVIOR.keyPasswordRejects();
      const wrong = reject !== undefined && keyPassword === reject;
      if (wrong) {
        const nextAttempts = keyAttempts + 1;
        setKeyAttempts(nextAttempts);
        const remaining = MOCK_BEHAVIOR.maxKeyAttempts - nextAttempts;
        if (remaining <= 0) {
          onReject(new SimulationError('KEY_ACCESS_DENIED'));
          return;
        }
        setKeyError(`Password incorrect — ${remaining} attempts remaining.`);
        setKeyPassword('');
        return;
      }
    }
    setKeyError(null);
    setActiveStep(2);
  };

  const handleWindowsSign = () => {
    const cert = selectedWindowsCert;
    if (!cert) return;
    setSubmitting(true);
    window.setTimeout(() => {
      onConfirm({ cert, pin: keyPassword });
    }, MOCK_BEHAVIOR.signLatencyMs);
  };

  const winProtection = selectedWindowsCert?.keyProtection ?? 'none';

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : handleCancel}
      aria-labelledby="cac-sim-dialog-title"
    >
      <DialogContainer sx={{ p: 2 }}>
        <Box
          sx={{
            pb: 1,
            borderBottom: (theme) =>
              `2px solid ${(theme.vars ?? theme).palette.divider}`,
          }}
        >
          <Typography variant="h5" id="cac-sim-dialog-title">
            Simulated signing — DEV/STAGING
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This dialog mimics the production signing flow. No physical card or
            real key is used; signatures are minted by the dev test CA.
          </Typography>
        </Box>

        <Box sx={{ pt: 2 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((s) => (
              <Step key={s.key}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ pt: 3, minHeight: 220 }}>
          {currentStep === 'choose' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Choose the credential to sign this {action} action. Only
                certificates valid for non-repudiation can sign.
              </Typography>
              <Stack spacing={1}>
                {windowsCerts.map((c) => {
                  const allowed = c.validForActions.includes(action);
                  const selected = selectedCredId === c.id;
                  return (
                    <SelectableRow
                      key={c.id}
                      selected={selected}
                      allowed={allowed}
                      onSelect={() => setSelectedCredId(c.id)}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: 'inherit' }}
                        >
                          {c.friendlyName ?? purposeLabel(c.purpose)}
                        </Typography>
                        <Chip
                          label="Windows"
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: 11 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {keyProtectionLabel(c.keyProtection)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: 'inherit', opacity: 0.85 }}
                      >
                        {c.subjectCN} · {c.issuerCN}
                      </Typography>
                    </SelectableRow>
                  );
                })}

                <SelectableRow
                  selected={selectedCredId === CAC_OPTION_ID}
                  allowed
                  onSelect={() => setSelectedCredId(CAC_OPTION_ID)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: 'inherit' }}
                    >
                      Common Access Card (CAC)
                    </Typography>
                    <Chip
                      label="Smart card"
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: 11 }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'inherit', opacity: 0.85 }}
                  >
                    Insert a physical card, then select an on-card certificate.
                  </Typography>
                </SelectableRow>
              </Stack>
            </Box>
          )}

          {currentStep === 'insert' && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Insert your CAC into the reader, then continue.
              </Typography>
              {readingCard && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Reading card…</Typography>
                </Box>
              )}
              {insertError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {insertError}
                </Alert>
              )}
            </Box>
          )}

          {currentStep === 'pick-cert' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select the certificate to sign this {action} action. Only the
                Signature certificate is valid for non-repudiation.
              </Typography>
              <Stack spacing={1}>
                {certs.map((c) => {
                  const allowed = c.validForActions.includes(action);
                  const selected = selectedCertId === c.id;
                  return (
                    <SelectableRow
                      key={c.id}
                      selected={selected}
                      allowed={allowed}
                      onSelect={() => setSelectedCertId(c.id)}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: 'inherit' }}
                        >
                          {purposeLabel(c.purpose)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {c.keyUsage}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: 'inherit', opacity: 0.85 }}
                      >
                        {c.subjectCN} · {c.issuerCN}
                      </Typography>
                    </SelectableRow>
                  );
                })}
              </Stack>
            </Box>
          )}

          {currentStep === 'enter-pin' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the PIN for{' '}
                {selectedCert?.subjectCN ?? 'the selected certificate'}.
              </Typography>
              <TextField
                autoFocus
                type="password"
                label="PIN"
                size="small"
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value);
                  if (pinError) setPinError(null);
                }}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 12,
                  autoComplete: 'off',
                }}
                disabled={submitting}
                sx={{ width: 220 }}
              />
              {pinError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {pinError}
                </Alert>
              )}
              {submitting && (
                <Box
                  sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    Authenticating to card…
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {currentStep === 'win-security' && selectedWindowsCert && (
            <Box>
              <Box
                sx={(theme) => {
                  const tokens = theme.vars ?? theme;
                  return {
                    border: `1px solid ${tokens.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                  };
                }}
              >
                <Box
                  sx={(theme) => {
                    const tokens = theme.vars ?? theme;
                    return {
                      px: 2,
                      py: 1,
                      backgroundColor: tokens.palette.action.hover,
                      borderBottom: `1px solid ${tokens.palette.divider}`,
                    };
                  }}
                >
                  <Typography variant="subtitle2">Windows Security</Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {winProtection === 'hello'
                      ? 'Verify your identity with Windows Hello to use the private key.'
                      : winProtection === 'password'
                        ? 'This certificate has strong private key protection. Enter the key password to allow signing.'
                        : 'Allow this app to use the private key for the selected certificate?'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedWindowsCert.friendlyName ??
                      purposeLabel(selectedWindowsCert.purpose)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedWindowsCert.subjectCN} ·{' '}
                    {selectedWindowsCert.issuerCN}
                  </Typography>

                  {winProtection === 'password' && (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        autoFocus
                        type="password"
                        label="Private key password"
                        size="small"
                        value={keyPassword}
                        onChange={(e) => {
                          setKeyPassword(e.target.value);
                          if (keyError) setKeyError(null);
                        }}
                        inputProps={{ maxLength: 64, autoComplete: 'off' }}
                        disabled={submitting}
                        sx={{ width: 280 }}
                      />
                    </Box>
                  )}

                  {winProtection === 'hello' && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      A Windows Hello prompt would appear here (PIN, fingerprint,
                      or face). Click “Use Windows Hello” to simulate approval.
                    </Alert>
                  )}

                  {keyError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {keyError}
                    </Alert>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {currentStep === 'win-sign' && selectedWindowsCert && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Ready to sign this {action} action with the selected Windows
                certificate.
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedWindowsCert.friendlyName ??
                  purposeLabel(selectedWindowsCert.purpose)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {providerLabel(selectedWindowsCert.provider)} ·{' '}
                {selectedWindowsCert.issuerCN}
              </Typography>
              {submitting && (
                <Box
                  sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2">Signing…</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            mt: 2,
            pt: 2,
            borderTop: (theme) =>
              `1px solid ${(theme.vars ?? theme).palette.divider}`,
          }}
        >
          <Button onClick={handleCancel} disabled={submitting} variant="outlined">
            Cancel
          </Button>

          {currentStep === 'choose' && (
            <Button
              onClick={handleChooseNext}
              variant="contained"
              disabled={!canChooseNext}
            >
              Next
            </Button>
          )}

          {currentStep === 'insert' && (
            <>
              <Button onClick={() => setActiveStep(0)} disabled={submitting}>
                Back
              </Button>
              <Button
                onClick={handleInsertNext}
                variant="contained"
                disabled={readingCard}
              >
                Next
              </Button>
            </>
          )}

          {currentStep === 'pick-cert' && (
            <>
              <Button onClick={() => setActiveStep(1)} disabled={submitting}>
                Back
              </Button>
              <Button
                onClick={handleCertNext}
                variant="contained"
                disabled={!canPickCert}
              >
                Next
              </Button>
            </>
          )}

          {currentStep === 'enter-pin' && (
            <>
              <Button onClick={() => setActiveStep(2)} disabled={submitting}>
                Back
              </Button>
              <Button
                onClick={handlePinSubmit}
                variant="contained"
                disabled={submitting || pin.length < 4}
              >
                {submitting ? 'Signing…' : 'Sign'}
              </Button>
            </>
          )}

          {currentStep === 'win-security' && (
            <>
              <Button onClick={() => setActiveStep(0)} disabled={submitting}>
                Back
              </Button>
              <Button
                onClick={handleWindowsAllow}
                variant="contained"
                disabled={submitting}
              >
                {winProtection === 'hello' ? 'Use Windows Hello' : 'Allow'}
              </Button>
            </>
          )}

          {currentStep === 'win-sign' && (
            <>
              <Button onClick={() => setActiveStep(1)} disabled={submitting}>
                Back
              </Button>
              <Button
                onClick={handleWindowsSign}
                variant="contained"
                disabled={submitting}
              >
                {submitting ? 'Signing…' : 'Sign'}
              </Button>
            </>
          )}
        </Box>
      </DialogContainer>
    </Modal>
  );
}
